/**
 * Rent Increases Page
 * Portfolio-wide list of every rent increase across the manager's leases (lease-lifecycle — The Rent
 * Increase Process, J-RI-PORTFOLIO). Each row names its property, unit and tenants and states the next
 * step (BR-LL-39); a second section lists active leases with no rent increase in progress, each with
 * the same "Plan a rent increase" button (eligibility is informational text only).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import {
  listRentIncreasesForPortfolio,
  listRentIncreaseCandidates,
  type RentIncreaseResponse,
  type RentIncreaseCandidate,
} from '../lib/api';
import { PageHeader, EmptyState } from '../components/layout/index';
import { useUnitTimezone } from '../hooks/useUnitTimezone';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  RentIncreaseStatusBadge,
  formatCurrency,
  formatRentIncreaseHeading,
  getNeedsAction,
} from '../components/rent-increase/RentIncreaseForm';
import { formatCalendarDate } from '../utils/date';
import { asCalendarDateString, todayInTimezone } from '@resida/shared';

type Status = RentIncreaseResponse['status'];

const STATUS_CHIPS: { value: Status; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** BR-LL-39: the list opens on what the landlord still has to act on. */
const DEFAULT_STATUSES: Status[] = ['DRAFT', 'ACTIVE'];

function formatDate(dateStr: string): string {
  return formatCalendarDate(asCalendarDateString(dateStr));
}

function percentChange(currentCents: number, newCents: number): string {
  if (currentCents <= 0) return '';
  const pct = ((newCents - currentCents) / currentCents) * 100;
  return ` (${pct >= 0 ? '+' : '-'}${Math.abs(pct).toFixed(1)}%)`;
}

function pluralize(count: number, word: string): string {
  return `${count} ${word}${count !== 1 ? 's' : ''}`;
}

/**
 * Eligibility is informational. "Now" versus "from <date>" is decided on the property's calendar day
 * (`today`); until the property's timezone is known the date is stated without claiming either.
 */
function describeEligibility(candidate: RentIncreaseCandidate, today: string | null): string {
  if (candidate.nextEligibleDate === null) {
    return candidate.hasPriorIncrease ? 'eligible now' : 'no prior increase on Resida';
  }
  if (today === null) return `next eligible ${formatDate(candidate.nextEligibleDate)}`;
  return candidate.nextEligibleDate <= today
    ? 'eligible now'
    : `eligible from ${formatDate(candidate.nextEligibleDate)}`;
}

type Load<T> = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: T };

export function RentIncreasesPage() {
  const [listLoad, setListLoad] = useState<Load<RentIncreaseResponse[]>>({ status: 'loading' });
  const [candidatesLoad, setCandidatesLoad] = useState<Load<RentIncreaseCandidate[]>>({ status: 'loading' });
  const [selectedStatuses, setSelectedStatuses] = useState<Status[]>(DEFAULT_STATUSES);

  // The two sections load independently: one failing request must not hide the other's data.
  const fetchList = useCallback(async () => {
    setListLoad({ status: 'loading' });
    try {
      const result = await listRentIncreasesForPortfolio();
      setListLoad({ status: 'ready', data: result.rentIncreases });
    } catch {
      setListLoad({ status: 'error' });
    }
  }, []);

  const fetchCandidates = useCallback(async () => {
    setCandidatesLoad({ status: 'loading' });
    try {
      const result = await listRentIncreaseCandidates();
      setCandidatesLoad({ status: 'ready', data: result.candidates });
    } catch {
      setCandidatesLoad({ status: 'error' });
    }
  }, []);

  useEffect(() => {
    fetchList();
    fetchCandidates();
  }, [fetchList, fetchCandidates]);

  const rentIncreases = useMemo(() => (listLoad.status === 'ready' ? listLoad.data : []), [listLoad]);

  function toggleStatus(status: Status) {
    setSelectedStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
    );
  }

  const visible = useMemo(() => {
    const matching = rentIncreases.filter((ri) => selectedStatuses.includes(ri.status));
    // Rows that need the landlord's action lead; the sort is stable so the server order holds otherwise.
    return matching
      .map((ri) => ({ ri, needsAction: getNeedsAction(ri) }))
      .sort((a, b) => Number(b.needsAction.length > 0) - Number(a.needsAction.length > 0));
  }, [rentIncreases, selectedStatuses]);

  const isDefaultFilter =
    selectedStatuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every((s) => selectedStatuses.includes(s));
  const selectedLabels = STATUS_CHIPS.filter((c) => selectedStatuses.includes(c.value)).map((c) => c.label);
  const showingText =
    selectedLabels.length === 0
      ? 'no statuses selected'
      : `showing ${selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.slice(0, -1).join(', ')} and ${selectedLabels[selectedLabels.length - 1]}`}`;

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="rent-increases-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Rent Increases"
          subtitle={listLoad.status === 'ready' ? `${pluralize(visible.length, 'rent increase')} · ${showingText}` : undefined}
          subtitleWrap
        />
      </div>

      {/* Status filter chips (multi-select; Draft and Active by default) */}
      <div
        role="group"
        aria-label="Status filters"
        className="px-[var(--page-padding-x)] mb-4 flex gap-1.5 overflow-x-auto scrollbar-none"
        data-testid="rent-increase-status-filters"
      >
        {STATUS_CHIPS.map((chip) => {
          const selected = selectedStatuses.includes(chip.value);
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={selected}
              onClick={() => toggleStatus(chip.value)}
              className={`shrink-0 px-3.5 rounded-full text-xs font-medium transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`status-filter-${chip.value}`}
            >
              {chip.label}
              {selected && <span aria-hidden="true"> ✓</span>}
            </button>
          );
        })}
      </div>

      {listLoad.status === 'loading' && <ListSkeleton testId="rent-increases-loading" />}

      {listLoad.status === 'error' && (
        <SectionError
          message="We couldn't load your rent increases."
          onRetry={fetchList}
          testId="rent-increases-error"
          retryTestId="rent-increases-retry-button"
        />
      )}

      {listLoad.status === 'ready' &&
        (visible.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title={
              selectedStatuses.length === 0
                ? 'No statuses selected'
                : isDefaultFilter
                  ? 'No rent increases in progress'
                  : `No ${selectedLabels.join(', ').toLowerCase()} rent increases`
            }
            description={
              selectedStatuses.length === 0
                ? 'Pick one or more statuses above to see rent increases.'
                : isDefaultFilter
                  ? 'Plan one below.'
                  : 'Try changing the filters.'
            }
            data-testid="rent-increases-empty"
          />
        ) : (
          <ul
            className="m-0 mx-[var(--page-padding-x)] flex list-none flex-col overflow-hidden rounded-lg border border-border bg-card p-0"
            aria-label="Rent increases"
            data-testid="rent-increases-list"
          >
            {visible.map(({ ri, needsAction }) => {
              const openStep = ri.steps.find((s) => s.status === 'PENDING' || s.status === 'OVERDUE');
              const action = needsAction[0];
              const tenantNames = ri.context.tenants.map((t) => t.name).join(', ');
              const scheduled = ri.nextScheduledEmailAt;
              return (
                <li key={ri.id} className="border-b border-border last:border-b-0">
                  <Link
                    to={`/rent-increases/${ri.id}`}
                    className="flex flex-col gap-1.5 px-4 py-3 min-h-[48px] hover:bg-accent transition-colors text-left w-full no-underline"
                    data-testid={`rent-increase-row-${ri.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="m-0 text-[15px] font-semibold text-foreground">
                          {formatRentIncreaseHeading(ri.context.property.name, ri.context.unit.unitNumber)}
                        </p>
                        <p className="m-0 text-[13px] text-muted-foreground" data-testid={`rent-increase-row-tenants-${ri.id}`}>
                          {tenantNames || 'No current tenants'}
                        </p>
                      </div>
                      <RentIncreaseStatusBadge
                        status={ri.status}
                        needsAction={needsAction.length > 0}
                        data-testid={`rent-increase-row-status-${ri.id}`}
                      />
                    </div>
                    <p className="m-0 text-sm text-foreground">
                      {formatCurrency(ri.currentRentAmountCents)} → {formatCurrency(ri.newRentAmountCents)}
                      <span className="text-muted-foreground">
                        {percentChange(ri.currentRentAmountCents, ri.newRentAmountCents)} · effective {formatDate(ri.effectiveDate)}
                      </span>
                    </p>
                    {action ? (
                      <p className="m-0 text-[13px] font-medium text-destructive" data-testid={`rent-increase-row-needs-action-${ri.id}`}>
                        Needs action:{' '}
                        {action.kind === 'BOUNCED'
                          ? `Email to ${action.tenantNames.join(', ')} bounced. Serve the ${action.stepLabel.replace(/^Serve /, '')} another way, then mark it served.`
                          : `${action.stepLabel} is overdue. Serve it, then mark it served.`}
                      </p>
                    ) : (
                      <p
                        className={`m-0 text-[13px] ${ri.warnings.length > 0 && ri.status === 'DRAFT' ? 'text-warning' : 'text-muted-foreground'}`}
                        data-testid={`rent-increase-row-next-${ri.id}`}
                      >
                        {ri.status === 'DRAFT' ? (
                          <>
                            {ri.warnings.length > 0 ? `${pluralize(ri.warnings.length, 'warning')} · ` : ''}
                            {ri.serviceMode === 'AUTOMATIC_EMAIL' ? 'Serving by automatic email' : 'Serving manually'} · Not yet activated
                          </>
                        ) : ri.status === 'ACTIVE' ? (
                          openStep ? (
                            <>
                              Next: {openStep.label} · due {formatDate(openStep.dueDate)}
                              {ri.serviceMode === 'AUTOMATIC_EMAIL' && scheduled && (
                                <>
                                  {' · '}
                                  <strong className="font-semibold text-foreground">emails automatically on {formatDate(scheduled)}</strong>
                                </>
                              )}
                              {ri.serviceMode === 'MANUAL' ? ' · Serving manually' : ''}
                              {ri.warnings.length > 0 && (
                                <span className="text-warning"> · {pluralize(ri.warnings.length, 'warning')}</span>
                              )}
                            </>
                          ) : (
                            'All steps are complete. The new rent starts on the effective date.'
                          )
                        ) : ri.status === 'COMPLETED' ? (
                          'Completed'
                        ) : (
                          'Cancelled'
                        )}
                      </p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        ))}

      <section className="mt-8 px-[var(--page-padding-x)]" aria-labelledby="rent-increase-candidates-heading" data-testid="rent-increase-candidates">
        <h2 id="rent-increase-candidates-heading" className="m-0 text-lg font-semibold text-foreground">
          Leases without a rent increase
        </h2>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">
          Active leases with no rent increase in progress. Pick a new rent and effective date to see the notices the law
          requires and when each is due. Nothing is sent until you activate it.
        </p>

        {candidatesLoad.status === 'loading' && <ListSkeleton testId="rent-increase-candidates-loading" flush />}

        {candidatesLoad.status === 'error' && (
          <SectionError
            message="We couldn't load your leases without a rent increase."
            onRetry={fetchCandidates}
            testId="rent-increase-candidates-error"
            retryTestId="rent-increase-candidates-retry-button"
            flush
          />
        )}

        {candidatesLoad.status === 'ready' &&
          (candidatesLoad.data.length === 0 ? (
            <p className="m-0 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="rent-increase-candidates-empty">
              Every active lease has a rent increase in progress.
            </p>
          ) : (
            <ul
              className="m-0 flex list-none flex-col overflow-hidden rounded-lg border border-border bg-card p-0"
              aria-label="Leases without a rent increase"
              data-testid="rent-increase-candidates-list"
            >
              {candidatesLoad.data.map((candidate) => (
                <CandidateRow key={candidate.leaseId} candidate={candidate} />
              ))}
            </ul>
          ))}
      </section>
    </div>
  );
}

/** One lease with no rent increase: who and what rent, when it is eligible, and the same plan button for all. */
function CandidateRow({ candidate }: { candidate: RentIncreaseCandidate }) {
  const headingId = `rent-increase-candidate-${candidate.leaseId}-heading`;
  const tenantNames = candidate.tenants.map((t) => t.name).join(', ');
  // The property's calendar day, not the browser's. Batched with the other rows into one request.
  const timezone = useUnitTimezone(candidate.unit.id).data;
  const today = timezone ? todayInTimezone(timezone) : null;
  return (
    <li
      className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`rent-increase-candidate-${candidate.leaseId}`}
    >
      <div className="min-w-0">
        <p id={headingId} className="m-0 text-[15px] font-semibold text-foreground">
          {formatRentIncreaseHeading(candidate.property.name, candidate.unit.unitNumber)}
        </p>
        <p className="m-0 text-[13px] text-muted-foreground">
          {[tenantNames || null, `${formatCurrency(candidate.currentRentAmountCents)}/mo`, describeEligibility(candidate, today)]
            .filter((part): part is string => part !== null)
            .join(' · ')}
        </p>
      </div>
      <Button asChild variant="outline" className="w-full sm:w-auto">
        <Link
          to={`/leases/${candidate.leaseId}/rent-increases/new`}
          state={{ from: 'rent-increases' }}
          aria-describedby={headingId}
          data-testid={`rent-increase-candidate-plan-${candidate.leaseId}`}
        >
          Plan a rent increase
        </Link>
      </Button>
    </li>
  );
}

/** The README's error-with-retry pattern, scoped to one section so the other keeps its data. */
function SectionError({
  message,
  onRetry,
  testId,
  retryTestId,
  flush = false,
}: {
  message: string;
  onRetry: () => void;
  testId: string;
  retryTestId: string;
  flush?: boolean;
}) {
  return (
    <div
      className={`${flush ? '' : 'mx-[var(--page-padding-x)] '}mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm`}
      role="alert"
      data-testid={testId}
    >
      <p className="text-destructive m-0">{message}</p>
      <Button onClick={onRetry} type="button" variant="outline" className="mt-2" data-testid={retryTestId}>
        Retry
      </Button>
    </div>
  );
}

function ListSkeleton({ testId, flush = false }: { testId: string; flush?: boolean }) {
  return (
    <div
      className={`${flush ? '' : 'mx-[var(--page-padding-x)] '}space-y-1`}
      aria-busy="true"
      aria-label="Loading"
      data-testid={testId}
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

export default RentIncreasesPage;
